"use client";

import { useTranslation } from "react-i18next";
import { useWizardStore, STEP_ORDER, type WizardStepKey } from "@/stores/wizard-store";
import { WizardProgressBar } from "./wizard-progress-bar";
import { StepWelcome } from "./steps/step-welcome";
import { StepBrandBasics } from "./steps/step-brand-basics";
import { StepBrandKit } from "./steps/step-brand-kit";
import { StepSocial } from "./steps/step-social";
import { StepTeam } from "./steps/step-team";
import { StepAiConfig } from "./steps/step-ai";
import { StepComplete } from "./steps/step-complete";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

async function fetchWizardState(brandId: string) {
  const res = await fetch(`/api/brands/${brandId}/wizard`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to fetch wizard state");
  return res.json() as Promise<{ wizardState: Record<string, string> }>;
}

async function patchWizardState(brandId: string, patch: Record<string, string>) {
  const res = await fetch(`/api/brands/${brandId}/wizard`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error("Failed to save wizard state");
  return res.json();
}

interface WizardModalProps {
  brandId: string;
}

export function WizardModal({ brandId }: WizardModalProps) {
  const { t } = useTranslation("wizard");
  const {
    open,
    currentStep,
    steps,
    closeWizard,
    nextStep,
    prevStep,
    markStep,
    setSteps,
    goToStep,
  } = useWizardStore();

  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["wizard", brandId],
    queryFn: () => fetchWizardState(brandId),
    enabled: !!brandId,
  });

  useEffect(() => {
    if (data?.wizardState) {
      setSteps(data.wizardState as Partial<typeof steps>);
    }
  }, [data, setSteps]);

  const { mutate: persist } = useMutation({
    mutationFn: (patch: Record<string, string>) => patchWizardState(brandId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wizard", brandId] }),
  });

  function handleMark(key: WizardStepKey, status: "complete" | "skipped") {
    markStep(key, status);
    persist({ [key]: status });
    nextStep();
  }

  function handleSkip() {
    const key = STEP_ORDER[currentStep - 1];
    if (key) handleMark(key, "skipped");
  }

  if (!open) return null;

  const isComplete = currentStep === 6;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeWizard();
      }}
    >
      <div className="w-full max-w-[600px] bg-background rounded-xl shadow-2xl flex flex-col overflow-hidden">
        <div className="p-7 flex flex-col gap-5">
          {!isComplete && (
            <>
              <WizardProgressBar currentStep={currentStep} />
              <div className="flex justify-between text-[11px]">
                <span className="text-brand font-medium">
                  {currentStep === 0 ? t("progress.overview") : t("progress.step", { n: currentStep, total: STEP_ORDER.length })}
                </span>
                <span className="text-muted-foreground">{t("progress.allOptional")}</span>
              </div>
            </>
          )}
          {isComplete && <WizardProgressBar currentStep={STEP_ORDER.length} complete />}

          {currentStep === 0 && (
            <StepWelcome
              steps={steps}
              onStart={() => nextStep()}
              onSkipAll={() => {
                const skipAll = Object.fromEntries(STEP_ORDER.map((k) => [k, "skipped"])) as Record<string, string>;
                STEP_ORDER.forEach((k) => markStep(k, "skipped"));
                persist(skipAll);
                goToStep(6);
              }}
            />
          )}
          {currentStep === 1 && (
            <StepBrandBasics brandId={brandId} onContinue={() => handleMark("basics", "complete")} onSkip={handleSkip} onBack={prevStep} />
          )}
          {currentStep === 2 && (
            <StepBrandKit brandId={brandId} onContinue={() => handleMark("brandKit", "complete")} onSkip={handleSkip} onBack={prevStep} />
          )}
          {currentStep === 3 && (
            <StepSocial brandId={brandId} onContinue={() => handleMark("social", "complete")} onSkip={handleSkip} onBack={prevStep} />
          )}
          {currentStep === 4 && (
            <StepTeam brandId={brandId} onContinue={() => handleMark("team", "complete")} onSkip={handleSkip} onBack={prevStep} />
          )}
          {currentStep === 5 && (
            <StepAiConfig brandId={brandId} onContinue={() => handleMark("aiConfig", "complete")} onSkip={handleSkip} onBack={prevStep} />
          )}
          {isComplete && <StepComplete onClose={closeWizard} />}
        </div>
      </div>
    </div>
  );
}
